import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { getEditorSize } from "lib/utils/getEditorSize";

function clampIndex(index, max) {
  if (!Number.isFinite(index) || max <= 0) return -1;
  return Math.min(Math.max(index, 0), max - 1);
}

function getCsvPointFromClick(csvOrigin, x, y, editorSize, file) {
  const rowCount = Array.isArray(csvOrigin) ? csvOrigin.length : 0;
  const colCount =
    rowCount > 0 && Array.isArray(csvOrigin[0]) ? csvOrigin[0].length : 0;

  if (rowCount <= 0 || colCount <= 0) {
    return { rowIndex: -1, colIndex: -1 };
  }

  const sourceSize = getEditorSize(csvOrigin, "thermal");
  x *= sourceSize.width / (editorSize?.width || sourceSize.width);
  y *= sourceSize.height / (editorSize?.height || sourceSize.height);

  if (rowCount === 435) {
    return {
      rowIndex: clampIndex(Math.floor(y), rowCount),
      colIndex: clampIndex(Math.floor(x), colCount),
    };
  }

  if (rowCount === 512) {
    const thermalOffsetX = Number(file?.thermal_offset_x) || 0;
    const thermalOffsetY = Number(file?.thermal_offset_y) || 0;
    const scale = 1.25;

    return {
      rowIndex: clampIndex(Math.floor((y - thermalOffsetY) / scale), rowCount),
      colIndex: clampIndex(Math.floor((x - thermalOffsetX) / scale), colCount),
    };
  }

  return {
    rowIndex: clampIndex(Math.floor(y), rowCount),
    colIndex: clampIndex(Math.floor(x), colCount),
  };
}

export default function TripleSpotOverlay({
  file,
  circles,
  setCircles,
  editorSize,
  type = "",
  disabled = false,
}) {
  const { t } = useTranslation();
  const [selectedIndex, setSelectedIndex] = useState(null);
  const overlayRef = useRef(null);
  const pointRefs = useRef([]);
  const selectedCircle = circles[selectedIndex];

  useEffect(() => {
    if (selectedIndex !== null) pointRefs.current[selectedIndex]?.focus({ preventScroll: true });
  }, [selectedIndex]);

  useEffect(() => {
    if (!selectedCircle) setSelectedIndex(null);
  }, [selectedCircle]);

  const getSize = () => ({
    width: overlayRef.current?.clientWidth || editorSize?.width || 640,
    height: overlayRef.current?.clientHeight || editorSize?.height || 435,
  });

  const removeSelected = () => {
    if (disabled || selectedIndex === null) return;
    setCircles((prev) => prev.map((circle, index) => index === selectedIndex ? undefined : circle));
    setSelectedIndex(null);
    overlayRef.current?.focus({ preventScroll: true });
  };

  const handleKeyDown = (event) => {
    if (disabled || event.altKey || event.ctrlKey || event.metaKey || !selectedCircle) return;
    const directions = { ArrowLeft: [-1, 0], ArrowRight: [1, 0], ArrowUp: [0, -1], ArrowDown: [0, 1] };
    if (event.key === "Delete" || event.key === "Backspace") {
      event.preventDefault();
      event.stopPropagation();
      removeSelected();
      return;
    }
    const direction = directions[event.key];
    if (!direction) return;
    event.preventDefault();
    event.stopPropagation();
    setCircles((prev) => {
      const circle = prev[selectedIndex];
      if (!circle) return prev;
      const moved = moveThermalSpot(circle, direction, file, getSize(), type);
      if (moved === circle) return prev;
      return prev.map((point, index) => index === selectedIndex ? moved : point);
    });
  };

  const handleClick = (e) => {
    if (disabled) return;
    const rect = e.currentTarget.getBoundingClientRect();
    if (!rect.width || !rect.height) return;
    const size = getSize();
    const x = (e.clientX - rect.left) * size.width / rect.width;
    const y = (e.clientY - rect.top) * size.height / rect.height;

    let value = "";
    if (type !== "masterImgEditor" && Array.isArray(file?.csv_origin)) {
      const { rowIndex, colIndex } = getCsvPointFromClick(
        file.csv_origin,
        x,
        y,
        editorSize,
        file,
      );
      const row = file.csv_origin[rowIndex];
      const rawValue = row?.[colIndex];

      if (typeof rawValue === "number" && Number.isFinite(rawValue)) {
        value = rawValue.toFixed(1);
      } else {
        return;
      }
    }

    const nextIndex = circles.findIndex((c) => !c);
    if (nextIndex === -1) return;
    setSelectedIndex(nextIndex);
    setCircles((prev) => {
      if (prev[nextIndex]) return prev;

      const newCircle =
        type === "masterImgEditor"
          ? {
              x,
              y,
              value: "",
            }
          : {
              x,
              y,
              value,
            };

      const updated = [...prev];
      updated[nextIndex] = newCircle;
      return updated;
    });
  };

  return (
    <div
      ref={overlayRef}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      className="tripleSpotOverlay"
      style={editorSize ? { width: editorSize.width, height: editorSize.height } : undefined}
      tabIndex={disabled ? -1 : 0}
      role="group"
      aria-label={t("imageFileIndex.threePhasePanel.selectedPoints")}
    >
      {circles.map((circle, index) =>
        circle ? (
          <button
            type="button"
            key={`triple-spot-${index}`}
            ref={(element) => { pointRefs.current[index] = element; }}
            disabled={disabled}
            aria-label={`L${index + 1}: ${circle.value} °C`}
            aria-pressed={selectedIndex === index}
            onFocus={() => setSelectedIndex(index)}
            onClick={(e) => {
              e.stopPropagation();
              setSelectedIndex(index);
              e.currentTarget.focus({ preventScroll: true });
            }}
            className="thermalCircles"
            style={{
              left: circle.x - 15,
              top: circle.y - 15,
              cursor: "pointer",
            }}
            data-value={`L${index + 1}: ${circle.value}`}
          >
            {selectedIndex === index && (
              <span className="thermalSpotSelectionRing" data-html2canvas-ignore="true" aria-hidden="true" />
            )}
          </button>
        ) : null,
      )}
      <div
        className="tripleSpotControls"
        data-html2canvas-ignore="true"
        onClick={(event) => event.stopPropagation()}
      >
        <div>
          {circles.map((circle, index) => (
            <button
              type="button"
              key={index}
              disabled={disabled || !circle}
              aria-pressed={Boolean(circle) && selectedIndex === index}
              onClick={() => {
                setSelectedIndex(index);
                pointRefs.current[index]?.focus({ preventScroll: true });
              }}
            >L{index + 1}</button>
          ))}
          <button type="button" disabled={disabled || !selectedCircle} onClick={removeSelected}>
            {t("tripleSpotEditor.remove")}
          </button>
          <span role="status">{selectedCircle ? `L${selectedIndex + 1}: ${selectedCircle.value} °C` : t("tripleSpotEditor.select")}</span>
        </div>
      </div>
    </div>
  );
}

// Stored coordinates stay in editor space; one step samples the adjacent CSV pixel.
export function moveThermalSpot(circle, [dx, dy], file, size, type = "") {
  if (!Number.isFinite(circle.x) || !Number.isFinite(circle.y)) return circle;
  const csv = file?.csv_origin;
  const scale = type !== "masterImgEditor" && csv?.length === 512 ? 1.25 : 1;
  let x = circle.x + dx * scale;
  let y = circle.y + dy * scale;
  let value = circle.value;
  if (type !== "masterImgEditor") {
    const { rowIndex, colIndex } = getCsvPointFromClick(csv, circle.x, circle.y, size, file);
    const row = rowIndex + dy;
    const col = colIndex + dx;
    if (rowIndex < 0 || colIndex < 0 || row < 0 || col < 0 || row >= csv.length || col >= (csv[row]?.length || 0)) return circle;
    const rawValue = csv[row][col];
    if (typeof rawValue !== "number" || !Number.isFinite(rawValue)) return circle;
    const offsetX = scale === 1.25 ? Number(file?.thermal_offset_x) || 0 : 0;
    const offsetY = scale === 1.25 ? Number(file?.thermal_offset_y) || 0 : 0;
    const sourceSize = getEditorSize(csv, "thermal");
    // Align the moved axis to the target pixel center, including offset/clipped images.
    if (dx) x = (offsetX + (col + 0.5) * scale) * size.width / sourceSize.width;
    if (dy) y = (offsetY + (row + 0.5) * scale) * size.height / sourceSize.height;
    value = rawValue.toFixed(1);
  }
  if (x < 0 || y < 0 || x >= size.width || y >= size.height) return circle;
  return { ...circle, x, y, value };
}
